import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import history from '../../utils/browserHistory';
import { localStorageHelper } from '../../utils/localStorage';
import { clearAuthStorage, getAuthRole } from '../../utils/authStorage';
import axios from 'axios';
import useTaskBadges from '../../utils/useTaskBadges';
import config from '../../config';
import { resetState } from '../../store/actionCreator';
import icons from '../../icons';
import { canOpenStaffPath } from '../../utils/staffAccess';
import { useTheme } from '../../context/ThemeContext';
import './sidebarNavigation.scss';

import { mcpEnabled } from '../../utils/mcp';
const SUPPORT_REQUESTS_ENABLED = process.env.REACT_APP_SUPPORT_REQUESTS === 'true';
const dueLabel = count => `${count} of my tasks due today or overdue`;
const adminNavElements = [
  {id:'finance-access',title:'Offboarding finance access',icon:<icons.payrollIcon/>,activeIcon:<icons.payrollIcon/>},
  {id:'permissions',title:'Permissions',icon:<icons.biggerLockIcon/>,activeIcon:<icons.biggerLockIcon/>},
  {id:'salary',title:'Salary',icon:<icons.payrollIcon/>,activeIcon:<icons.payrollIcon/>},
  { id: 'dashboard', title: 'Dashboard', icon: <icons.dashboardInactive />, activeIcon: <icons.dashboard /> },
  { id: 'tasks/new/inbox', title: 'Inbox', icon: <icons.bellIcon />, activeIcon: <icons.bellIcon /> },
  {
    id: 'people',
    title: 'People',
    icon: <icons.employeeInactive />,
    activeIcon: <icons.employeeActive />,
    children: [
      { id: 'customers', title: 'Stakeholders' },
      { id: 'talents', title: 'Employees' },
      { id: 'organizations', title: 'Customers' },
      { id: 'agencies', title: 'Agency' },
      { id: 'feedback', title: 'Feedback' }
    ]
  },
  {
    id: 'work',
    title: 'Work',
    icon: <icons.tasksActive />,
    activeIcon: <icons.tasksInactive />,
    children: [
      { id: 'tasks/new', title: 'Tasks', badge: 'tasks' },
      { id: 'on-off-boarding', title: 'On/Off boarding' },
      { id: 'manual', title: 'Manual' }
    ]
  },
  { id: 'reports', title: 'Reports', icon: <icons.reportInactive />, activeIcon: <icons.reportActive /> },
  { id: 'payroll-hub', title: 'Payroll Hub', icon: <icons.payrollIcon />, activeIcon: <icons.payrollIcon /> },
  { id: 'devices', title: 'Device Balance', icon: <icons.deviceBalanceIcon />, activeIcon: <icons.deviceBalanceIcon /> },
  {
    id: 'admin',
    title: 'Admin',
    icon: <icons.biggerLockIcon />,
    activeIcon: <icons.biggerLockIcon />,
    children: [
      { id: 'users', title: 'Users' },
      { id: 'on-off-boarding-templates', title: 'On/Off boarding templates' },
      { id: 'admin/mass-email', title: 'Mass Email' },
      { id: 'activity-log', title: 'Activity Log' },
      { id: 'telegram-bot', title: 'Telegram Bot' },
      { id: 'integrations', title: 'Integrations' },
      { id: 'settings', title: 'Settings' },
      { id: 'account', title: 'Account' },
      ...(mcpEnabled() ? [{ id: 'ai-integrations', title: 'AI Access' }] : []),
      // Backend /support-requests is not delivered yet; hidden unless REACT_APP_SUPPORT_REQUESTS=true.
      ...(SUPPORT_REQUESTS_ENABLED ? [{ id: 'support-requests', title: 'Requests' }] : [])
    ]
  }
];

const talentNavElements = [
  { id: 'talent/profile', title: 'My Profile', icon: <icons.user />, activeIcon: <icons.user /> },
  { id: 'talent/my-finance', title: 'My Finance', icon: <icons.reportInactive />, activeIcon: <icons.reportActive /> },
  { id: 'talent/hours-reporting', title: 'Hours Reporting', icon: <icons.calendarChecked />, activeIcon: <icons.calendarChecked /> }
];

const talentAccountNavElement = {
  id: 'talent/account',
  title: 'Account',
  icon: <icons.biggerLockIcon />,
  activeIcon: <icons.biggerLockIcon />
};

const isHrsComsEnabled = value => value === true || value === 1 || value === '1' || value === 'true';

const SideBar = ({ userType, collapsed = false, onToggleCollapse = () => {} }) => {
  const user = useSelector(state => state.user);
  const { theme, toggleTheme } = useTheme();
  const dispatch = useDispatch();

  const logout = async () => {
    const sidebarCollapsed = localStorageHelper.getItem('sidebarCollapsed');
    try {
      await axios.post(`${config.API_URL}/logout`);
    } catch {
      // Cookie should still be cleared client-side redirect even if request fails.
    }
    clearAuthStorage();
    if (sidebarCollapsed) {
      localStorageHelper.setItem('sidebarCollapsed', sidebarCollapsed);
    }
    dispatch(resetState());
    history.push('/login');
  };

  const userRole = user?.role || getAuthRole() || localStorageHelper.getItem('role');
  const taskBadges = useTaskBadges(['Administrator', 'Finance', 'HR', 'Account Executive', 'Agency'].includes(userRole));
  const [openGroups, setOpenGroups] = useState({});
  const [compact, setCompact] = useState(() => window.matchMedia?.('(max-width: 639px)').matches || false);
  const [flyout, setFlyout] = useState(null);
  const closeFlyout = () => {
    if (flyout) document.getElementById(flyout.id)?.focus();
    setFlyout(null);
  };
  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 639px)');
    const update = () => { setCompact(media.matches); setFlyout(null); };
    media?.addEventListener('change', update);
    return () => media?.removeEventListener('change', update);
  }, []);
  useEffect(() => { setFlyout(null); }, [collapsed, userRole]);
  useEffect(() => {
    if (!flyout) return undefined;
    document.getElementById('sidebar-group-panel')?.querySelector('button[data-nav]')?.focus();
    const escape = event => { if (event.key === 'Escape') { event.preventDefault(); document.getElementById(flyout.id)?.focus(); setFlyout(null); } };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [flyout]);

  const getNavElements = () => {
    if (userType === 'talent') return talentNavElements.filter(el => {
      if (el.id === 'talent/hours-reporting') return isHrsComsEnabled(user?.hrsComs);
      if (el.id === 'talent/my-finance') return user?.financeAccessEnabled !== false;
      return true;
    });
    const allowed = el => canOpenStaffPath({ ...user, role: userRole }, '/' + el.id);
    return adminNavElements.map(el => el.children
      ? { ...el, children: el.children.filter(allowed) }
      : el).filter(el => el.children ? el.children.length : allowed(el));
  };

  const navElements = getNavElements();
  const activePath = history.location.pathname.slice(1);
  const isInboxActive = activePath === 'tasks/new/inbox';
  const isTalentAccountActive = talentAccountNavElement.id === activePath;
  const isChildActive = id => {
    if (id === 'tasks') return activePath === 'tasks';
    if (id === 'tasks/new') return activePath.startsWith('tasks/new') && !isInboxActive;
    return id === activePath;
  };
  const isNavItemActive = (id, children) => {
    if (children?.some(child => isChildActive(child.id))) return true;
    return id === activePath;
  };
  const initials = user?.fullName
    ? user.fullName
      .split(' ')
      .map(w => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()
    : '?';

  return (
    <aside
      className={`coms-real-sidebar ${collapsed ? 'is-collapsed' : 'is-expanded'} fixed bottom-0 left-0 top-auto h-16 flex flex-row z-40 bg-coms-surface border-t border-coms-line transition-all duration-200 sm:top-0 sm:bottom-auto sm:h-screen sm:flex-col sm:border-r sm:border-t-0 ${
        collapsed ? 'w-full sm:w-16' : 'w-full sm:w-[180px]'
      }`}
    >
      <div className={`hidden py-4 flex-shrink-0 items-center sm:flex ${collapsed ? 'px-2 justify-center gap-2' : 'px-3 justify-center sm:justify-between gap-2'}`}>
        <div className='min-w-0 flex flex-1 items-center'>
          {collapsed ? (
            <div className='coms-real-logo coms-real-logo--short'>CO</div>
          ) : (
            <div className='coms-real-logo'><b>COMMIT</b><strong>OFFSHORE</strong><span>· COMS</span></div>
          )}
        </div>
        <button
          type='button'
          onClick={onToggleCollapse}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={`hidden sm:flex transition-all duration-150 ${
            collapsed
              ? 'w-10 h-10 items-center justify-center rounded-xl border border-coms-accent-soft bg-coms-accent-soft text-coms-accent shadow-sm hover:brightness-95'
              : 'w-9 h-9 items-center justify-center rounded-xl border border-coms-line bg-coms-surface text-coms-ink-3 shadow-sm hover:border-coms-accent-soft hover:bg-coms-accent-soft hover:text-coms-accent'
          }`}
        >
          <span className={`flex items-center justify-center ${collapsed ? 'w-8 h-8 rounded-lg' : 'w-5 h-5'}`}>
            {collapsed ? <icons.arrowRight /> : <icons.arrowLeft />}
          </span>
        </button>
      </div>

      <div className={`hidden h-px bg-coms-line flex-shrink-0 sm:block ${collapsed ? 'mx-3' : 'mx-5'}`} />

      <nav className={`app-sidebar-nav flex flex-1 flex-row overflow-x-auto px-2 py-2 scrollbar-none sm:block sm:overflow-x-visible sm:px-2.5 sm:py-3 ${userType === 'talent' ? 'sm:flex-none' : 'sm:flex-1'}`}>
        {navElements.map(({ id, title, icon, activeIcon, children }) => {
          const hasChildren = Array.isArray(children) && children.length > 0;
          const isActive = isNavItemActive(id, children);
          const isOpen = hasChildren && (openGroups[id] ?? isActive);
          return (
            <React.Fragment key={id}>
            <button
              type='button'
              id={id}
              aria-expanded={hasChildren ? (compact || collapsed ? flyout?.id === id : isOpen) : undefined}
              aria-controls={hasChildren ? (compact || collapsed ? 'sidebar-group-panel' : 'sidebar-group-' + id) : undefined}
              onClick={event => {
                if (hasChildren) {
                  if (compact || collapsed) {
                    const top = Math.max(8, Math.min(event.currentTarget.getBoundingClientRect().top, window.innerHeight - 320));
                    setFlyout(current => current?.id === id ? null : { id, title, children, top });
                  } else setOpenGroups(prev => ({ ...prev, [id]: !isOpen }));
                } else { setFlyout(null); history.push(`/${id}`); }
              }}
              title={collapsed ? title : ''}
              aria-label={title}
              className={`
                app-sidebar-item text-left sm:w-full relative flex min-w-[52px] items-center px-3 py-2 rounded-xl mb-0.5 cursor-pointer
                transition-all duration-150 select-none group
                flex-shrink-0
                ${collapsed ? 'justify-center gap-0' : 'justify-center sm:justify-start gap-0 sm:gap-3'}
                ${isActive ? 'bg-coms-accent-soft text-coms-accent' : 'text-coms-ink-3 hover:bg-coms-surface-2 hover:text-coms-ink'}
              `}
            >
              {isActive && (
                <span className={`hidden sm:block absolute left-0 top-1/2 -translate-y-1/2 bg-coms-accent rounded-r-full ${collapsed ? 'w-1 h-8' : 'w-1 h-5'}`} />
              )}

              <span
                className={`flex-shrink-0 transition-colors ${isActive ? 'text-coms-accent' : 'text-coms-ink-3 group-hover:text-coms-ink'}`}
              >
                {isActive ? activeIcon : icon}
              </span>

              {!collapsed && (
                <span className={`hidden sm:inline text-[12px] font-medium transition-colors ${isActive ? 'text-coms-accent font-semibold' : ''}`}>
                  {title}
                </span>
              )}

              {!collapsed && id === 'tasks/new/inbox' && taskBadges.inbox > 0 && (
                <span className={`hidden sm:block ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${isActive ? 'bg-coms-accent text-coms-accent-ink' : 'bg-coms-accent-soft text-coms-accent'}`}>{taskBadges.inbox}</span>
              )}
              {isActive && !collapsed && id !== 'tasks/new/inbox' && <span className='hidden sm:block ml-auto w-1.5 h-1.5 rounded-full bg-coms-accent opacity-60' />}
            </button>
            {hasChildren && isOpen && !collapsed && !compact && (
              <div id={'sidebar-group-' + id} className='hidden sm:block mb-1 ml-9 space-y-1'>
                {children.map(child => (
                  <button
                    key={child.id}
                    type='button'
                    onClick={() => history.push(`/${child.id}`)}
                    className={`flex w-[calc(100%-0.5rem)] items-center justify-between rounded-lg px-3 py-1.5 text-left text-[11px] font-semibold ${isChildActive(child.id) ? 'bg-coms-accent-soft text-coms-accent' : 'text-coms-ink-3 hover:bg-coms-surface-2'}`}
                  >
                    <span>{child.title}</span>
                    {child.badge === 'tasks' && taskBadges.due > 0 && <span title={dueLabel(taskBadges.due)} aria-label={dueLabel(taskBadges.due)} className='rounded-full bg-coms-warn-soft px-1.5 py-0.5 text-[10px] font-semibold text-coms-warn'>{taskBadges.due}</span>}
                  </button>
                ))}
              </div>
            )}
            </React.Fragment>
          );
        })}
      </nav>
      {flyout && <>
        <div className='sidebar-group-backdrop' onClick={closeFlyout} />
        <nav id='sidebar-group-panel' aria-label={flyout.title + ' sections'} className='sidebar-group-panel' style={{ '--sidebar-panel-top': flyout.top + 'px' }}>
          <div className='sidebar-group-panel__heading'><strong>{flyout.title}</strong><button type='button' aria-label='Close navigation' onClick={closeFlyout}>×</button></div>
          {flyout.children.map(child => <button data-nav key={child.id} type='button' aria-current={isChildActive(child.id) ? 'page' : undefined} onClick={() => { setFlyout(null); history.push('/' + child.id); }}>{child.title}{child.badge === 'tasks' && taskBadges.due > 0 ? <span title={dueLabel(taskBadges.due)} aria-label={dueLabel(taskBadges.due)}>{' (' + taskBadges.due + ')'}</span> : ''}</button>)}
        </nav>
      </>}

      <div className='flex-shrink-0 border-l border-coms-line p-2 sm:mt-auto sm:border-l-0 sm:border-t sm:p-2.5'>
        {user?.fullName && (
          <div
            className={`hidden px-3 py-2.5 mb-1 rounded-xl bg-coms-surface-2 sm:flex ${collapsed ? 'justify-center' : 'items-center gap-2.5'}`}
            title={collapsed ? user.fullName : ''}
          >
            <div className='w-8 h-8 rounded-full bg-coms-accent-soft flex items-center justify-center flex-shrink-0 ring-2 ring-coms-surface'>
              <span className='text-coms-accent font-bold text-[12px]'>{initials}</span>
            </div>
            {!collapsed && (
              <div className='hidden sm:block min-w-0 flex-1'>
                <p className='text-[12px] font-semibold text-coms-ink truncate leading-tight'>{user.fullName}</p>
                <p className='text-[10px] text-coms-ink-3 capitalize mt-0.5'>{userType}</p>
              </div>
            )}
          </div>
        )}

        {userType === 'talent' && (
          <button
            type='button'
            onClick={() => history.push(`/${talentAccountNavElement.id}`)}
            title={collapsed ? talentAccountNavElement.title : ''}
            aria-label={talentAccountNavElement.title}
            className={`relative mb-1 flex w-full min-w-[52px] items-center rounded-xl px-3 py-2 text-left transition-all duration-150 group ${
              collapsed ? 'justify-center gap-0' : 'justify-center sm:justify-start gap-0 sm:gap-3'
            } ${
              isTalentAccountActive ? 'bg-coms-accent-soft text-coms-accent' : 'text-coms-ink-3 hover:bg-coms-surface-2 hover:text-coms-ink'
            }`}
          >
            {isTalentAccountActive && (
              <span className={`hidden sm:block absolute left-0 top-1/2 -translate-y-1/2 bg-coms-accent rounded-r-full ${collapsed ? 'w-1 h-8' : 'w-1 h-5'}`} />
            )}
            <span className={`flex-shrink-0 transition-colors ${isTalentAccountActive ? 'text-coms-accent' : 'text-coms-ink-3 group-hover:text-coms-ink'}`}>
              {isTalentAccountActive ? talentAccountNavElement.activeIcon : talentAccountNavElement.icon}
            </span>
            {!collapsed && (
              <span className={`hidden sm:inline text-[12px] font-medium transition-colors ${isTalentAccountActive ? 'text-coms-accent font-semibold' : ''}`}>
                {talentAccountNavElement.title}
              </span>
            )}
            {isTalentAccountActive && !collapsed && <span className='hidden sm:block ml-auto w-1.5 h-1.5 rounded-full bg-coms-accent opacity-60' />}
          </button>
        )}

        <button
          onClick={toggleTheme}
          title={collapsed ? (theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode') : ''}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className={`w-auto sm:w-full px-2 sm:px-3 py-2 mb-0 sm:mb-1 rounded-xl text-coms-ink-3 hover:bg-coms-surface-2 hover:text-coms-ink transition-all duration-150 group ${
            collapsed ? 'flex justify-center' : 'flex items-center gap-3'
          }`}
        >
          <span className='flex-shrink-0 transition-colors'>
            {theme === 'dark' ? (
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <circle cx='12' cy='12' r='4' />
                <path d='M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41' />
              </svg>
            ) : (
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z' />
              </svg>
            )}
          </span>
          {!collapsed && <span className='hidden sm:inline text-[12px] font-medium'>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
        </button>

        <button
          onClick={() => { const event = new CustomEvent('hms:request-leave', { cancelable: true, detail: logout }); if (window.dispatchEvent(event)) logout(); }}
          title={collapsed ? 'Log Out' : ''}
          aria-label='Log Out'
          className={`w-full px-3 py-2 rounded-xl text-coms-ink-3 hover:bg-coms-accent-soft hover:text-coms-accent transition-all duration-150 group ${
            collapsed ? 'flex justify-center' : 'flex items-center gap-3'
          }`}
        >
          <span className='flex-shrink-0 transition-colors group-hover:text-coms-accent'>
            <icons.logout />
          </span>
          {!collapsed && <span className='hidden sm:inline text-[12px] font-medium'>Log Out</span>}
        </button>
      </div>
    </aside>
  );
};

export default SideBar;
